/**
 * Pure display helpers for a model version's training run (the contract's
 * `TrainingRun` read projection). Shared by the model-detail training-time card
 * and the executions-chain strip so both render wall-clock duration and billed
 * minutes identically. Every figure comes from the recorded run row / usage
 * ledger — these functions only format, never estimate.
 */

/**
 * Human wall-clock duration for a training run. Null/undefined (a run still
 * queued or running, with no settled duration) renders the honest em dash;
 * sub-minute reads in seconds, under an hour in whole minutes, longer as
 * "Nh Mm".
 */
export function formatRunDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMinutes = Math.floor(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * Billed training minutes, under a column already headed "Billed". Null/undefined (the run has not
 * settled its charge yet) renders the honest em dash — never a fabricated zero.
 */
export function formatBilledMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  return `${minutes.toLocaleString()} min`;
}

/**
 * Why a training run ended, in the customer's words. The trainer records the
 * reason in the run's metrics (`stopped_reason`, W7-GT-01 T8(b)) — a run that
 * ends before its epoch cap does so because the eval loss stopped improving,
 * and the page says which fact ended it rather than leaving a short run looking
 * like a complete one. An unrecorded or unrecognised reason renders nothing:
 * there is no honest sentence to write about a run that did not say why it
 * ended.
 */
export function trainingStopNote(
  reason: unknown,
  epochsCompleted: number | null | undefined,
  epochsPlanned: number | null | undefined,
): string | null {
  const completed =
    typeof epochsCompleted === "number" ? formatEpochsCompleted(epochsCompleted) : null;
  const planned = typeof epochsPlanned === "number" && epochsPlanned > 0 ? epochsPlanned : null;
  switch (reason) {
    case "plateau":
      return completed !== null && planned !== null
        ? `Stopped at epoch ${completed} of ${planned} — the model stopped improving.`
        : "Stopped early — the model stopped improving.";
    case "epoch_cap":
      return planned !== null ? `Ran all ${planned} epochs.` : "Ran to the epoch limit.";
    case "step_target":
      return "Stopped at the step limit.";
    default:
      return null;
  }
}

/**
 * Epochs a run actually COMPLETED, as the page states them: a whole number when the
 * run landed on one, a single decimal otherwise (a run that ends mid-epoch really did
 * stop there). Distinct from `hyperparams.formatEpochs`, which renders the whole-number
 * epoch knob the run was configured with.
 */
export function formatEpochsCompleted(value: number | null | undefined): string {
  if (typeof value !== "number") return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
